import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  Server,
  identityClient,
  publicIdentityClient,
} from '@wajeez-web/core/api-base';
import { ApiMethod } from '@wajeez-web/stores/base';
import { AlertType, CommonActions } from '@wajeez-web/stores/common';
import { updateUser, getUser, AuthActions } from '@wajeez-web/stores/auth';
import {
  thunkErrorHandler,
  thunkErrorHandlerWithoutSdk,
  thunkResponseChecker,
  thunkResponseCheckerSdk,
} from '@wajeez-web/core/utils';
import { AlertInput } from './types';

export const name = 'account';

export const linkGoogle: ApiMethod<
  Server.LinkGoogleLoginInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_link_google_post_V1(data);

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const linkGoogleAsync = createAsyncThunk(
  name + '/linkGoogle',
  linkGoogle
);

export const linkGoogleAccessToken: ApiMethod<
  Server.LinkGoogleLoginInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_link_google_post_V2(data);

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const linkGoogleAccessTokenAsync = createAsyncThunk(
  name + '/linkGoogleAccessToken',
  linkGoogleAccessToken
);
export const linkTwitter: ApiMethod<
  Server.LinkTwitterLoginV2Input & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_link_twitter_post_V2(data);

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const linkTwitterAsync = createAsyncThunk(
  name + '/linkTwitter',
  linkTwitter
);

export const linkApple: ApiMethod<
  Server.LinkAppleLoginInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_link_apple_post_V1(data);

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const linkAppleAsync = createAsyncThunk(name + '/linkApple', linkApple);

export const linkFacebook: ApiMethod<
  Server.LinkFacebookLoginInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_link_facebook_post_V1(data);

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const linkFacebookAsync = createAsyncThunk(
  name + '/linkFacebook',
  linkFacebook
);

export const getAuthProviders: ApiMethod<
  AlertInput,
  Server.AuthProviderOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_authProviders_get_V1();

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getAuthProvidersAsync = createAsyncThunk(
  name + '/getAuthProviders',
  getAuthProviders
);

export const setAutoplay: ApiMethod<
  { disabled: boolean } & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_autoplay_put_V1(data);

    if (response.success) {
      const user = getUser();

      updateUser({ ...user, autoPlayDisabled: data.disabled });

      dispatch(AuthActions.setAutoplayDisabled(data.disabled));
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const setAutoplayAsync = createAsyncThunk(
  name + '/setAutoplay',
  setAutoplay
);

export const setNextTitle: ApiMethod<
  { disabled: boolean } & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_nextTitle_put_V1(data);

    if (response.success) {
      const user = getUser();

      updateUser({ ...user, nextTitleDisabled: data.disabled });

      dispatch(AuthActions.setNextTitleDisabled(data.disabled));
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const setNextTitleAsync = createAsyncThunk(
  name + '/setNextTitle',
  setNextTitle
);

export const setPasswordByInvitationToken: ApiMethod<
  Server.SetUserPasswordByTokenInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_setPasswordByToken_post_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const setPasswordByInvitationTokenAsync = createAsyncThunk(
  name + '/setPasswordByInvitationToken',
  setPasswordByInvitationToken
);

export const registerToBusinessGroup: ApiMethod<
  { password: string; email: string; businessGroupVoucher: string },
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_register_toBusinessGroup_post_V1(
      data
    );

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log(error);

    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const registerToBusinessGroupAsync = createAsyncThunk(
  name + '/registerToBusinessGroup',
  registerToBusinessGroup
);

export const getUserCountry: ApiMethod<void, string, void> = async (
  _,
  { dispatch, rejectWithValue }
) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseChecker(async () => {
        return publicIdentityClient.get(
          'api/identity/v1/General/get-user-country-code-response'
        );
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const getUserCountryAsync = createAsyncThunk(
  name + '/getUserCountryAsync',
  getUserCountry
);

export const onboardingCompleted: ApiMethod<void, void, void> = async (
  _,
  { dispatch, rejectWithValue }
) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_Account_Client(
          undefined,
          identityClient
        );

        return client.account_onboardingComplteted_put_V1({});
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const onboardingCompletedAsync = createAsyncThunk(
  name + '/onboardingCompletedAsync',
  onboardingCompleted
);

export const addOnboardingQuestionnaire: ApiMethod<
  Server.SaveOnboardingQuestionnaireCommand,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandlerWithoutSdk(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_Account_Client(
          undefined,
          identityClient
        );

        return client.account_onboardingQuestionnaire_post_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const addOnboardingQuestionnaireAsync = createAsyncThunk(
  name + '/addOnboardingQuestionnaireAsync',
  addOnboardingQuestionnaire
);
